import { DrugIcd, Prescription } from "@fusion-advantage/backcompat"
import { findIndexFrom, findPreviousIndexFrom, DrugIcdCodeDataSet, medReconItemCanAddIndication } from "@fusion-advantage/formlib"
import { L2Dialog, L2DialogActions } from "../l2/L2Dialog"
import { L1ReasonableButton, L1SmallIconButton } from "../l1/L1Button"
import { faAngleLeft, faAngleRight, faMagnifyingGlass, faPlus, faTrashCan, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FusionColor } from "../../../theme/FusionStyle"
import _ from 'lodash'
import L1TextField from "../l1/L1TextField"
import { ComplexElementSize } from "../ComplexElementSize"
import { SelectedResultRow, CollapsedContentContainer, MedReconDialogContentContainer, SearchResultRow, SearchResultRowActions, SearchResultRowText, SearchResultsContainer, SearchResultsList, SuggestedResult } from "../l1/L1MedicationReconcilliation"
import L3LazyLoadedList from "./L3LazyLoadedList"

type L3MedReconIndicationsModalProps = {
	models: Prescription[],
	show: boolean,
	initialId: number|null,
	onClose: () => void,
	onSave: (m: Prescription[]) => void,
	handleLoadProductCodes: (productId: number) => Promise<DrugIcd[]>,
	icdDataSet: DrugIcdCodeDataSet
}

enum ModalNavEnum {
	Next,
	Previous
}

const L3MedReconIndicationsModal = ({models, handleLoadProductCodes, show, onClose, onSave, icdDataSet, initialId}: L3MedReconIndicationsModalProps) => {
	const [currentIdx, setCurrentIdx] = useState(0)
	const [internalModels, setModelState] = useState<Prescription[]>([])
	const [searchTerm, setSearchTerm] = useState('')
	const [searchResults, setSearchResults] = useState<DrugIcd[]>([])
	const [currentProductCodes, setProductCodes] = useState<DrugIcd[]>([])
	const textFieldRef = useRef<HTMLInputElement>(null)

	const { nextIndex, prevIndex, hasNext, hasPrev, currentModel} = useMemo(() => {
		const nextIndex = findIndexFrom(internalModels, (model: Prescription) => medReconItemCanAddIndication(model), currentIdx + 1)
		const prevIndex = findPreviousIndexFrom(internalModels, (model: Prescription) => medReconItemCanAddIndication(model), currentIdx - 1)
		const hasNext = nextIndex > -1
		const hasPrev = prevIndex > -1
		const currentModel = internalModels[currentIdx]
	
		return { nextIndex, prevIndex, hasNext, hasPrev, currentModel }
	}, [currentIdx, internalModels, initialId, icdDataSet, models])

	const debouncedSearchTerm = useRef(
		_.debounce((term: string, codes: DrugIcd[]) => {
			if (!term) return;
			const results = codes.filter(code =>
				code.Description?.toLowerCase().includes(term.toLowerCase())
			);
			setSearchResults(results);
		}, 300)
	);

	useEffect(() => {
		if(show && initialId !== null) {
			const index = models.findIndex(x => x.Product.ID === initialId)

			setCurrentIdx(index > -1 ? index : 0)
		}

		textFieldRef.current?.focus()
	}, [initialId])

	useEffect(() => {
		syncLocalModelState(models, internalModels)
	}, [models])

	useEffect(() => {
		if(currentModel) {
			const loadCodes = async () => {
				const codes = await handleLoadProductCodes(currentModel.Product.ID)
				setProductCodes(codes)
				setSearchResults(codes)
			}
		
			loadCodes()
		}
	}, [currentModel])
	
	useEffect(() => {
		if(!searchTerm) {
			debouncedSearchTerm.current.cancel()
			return setSearchResults(currentProductCodes)
		}

		debouncedSearchTerm.current(searchTerm, icdDataSet.allIcdCodes)
	}, [searchTerm, icdDataSet.allIcdCodes, currentProductCodes])

	const syncLocalModelState = useCallback((savedModels: Prescription[], localStateModels: Prescription[]) => {
		if(localStateModels.length === 0) {
			setModelState(_.cloneDeep(savedModels))
		}

		const localMap = _.keyBy(localStateModels, 'Product.ID')
		const temp = _.cloneDeep(savedModels)
		
		_.forEach(temp, (model) => {
			const localIndications = localMap[model.Product.ID]?.Indications ?? []

			// if auto save happens while modal is open, new models from redux will trigger a re-render
			// and would cause the user to lose any changes made to local model state. this prevents this from happening
			if(show) {
				model.Indications = [...localIndications]
			}
		})
		
		const updatedIdx = temp.findIndex(x => x.Product.ID === currentModel?.Product.ID)

		setModelState(temp)
		setCurrentIdx(updatedIdx)
	}, [currentModel, show])

	const hasIndication = useCallback((icd: string) => {
		return currentModel?.Indications
			.map(x => x.Icd10Code)
			.includes(icd)
	}, [icdDataSet, currentModel])

	const isSuggested = useCallback((icd: string) => {
		return currentProductCodes.map(x => x.Icd10Code).includes(icd)
	}, [currentProductCodes])

	const handleNavigate = useCallback((direction: ModalNavEnum) => {
		switch(direction) {
			case ModalNavEnum.Next:
				setCurrentIdx(nextIndex)
				break
			case ModalNavEnum.Previous:
				setCurrentIdx(prevIndex)
				break
		}

		setSearchTerm('')
	}, [nextIndex, prevIndex])

	const handleChange = useCallback((icd: string, isManual: boolean = false) => {
		const code = icdDataSet.icdCodeMap[icd] || currentProductCodes.find(x => x.Icd10Code === icd)

		if(hasIndication(icd) === false && code) {
			currentModel.Indications.push({ Icd10Code: code.Icd10Code, Description: code.Description, IsManual: isManual })
		}
		else {
			currentModel.Indications = currentModel.Indications.filter(x => x.Icd10Code !== code.Icd10Code)
		}

		const modelsTemp = _.cloneDeep(internalModels)
		modelsTemp[currentIdx] = currentModel

		setModelState(modelsTemp)
	}, [hasIndication, currentModel, currentProductCodes, currentIdx, internalModels])

	const handleSave = useCallback(() => {
		onSave(internalModels)
		onClose()
		setSearchTerm('')
	}, [onSave, internalModels])

	/*
	const handleClose = useCallback(() => {
		onClose()
		setSearchTerm('')
		setModelState(_.cloneDeep(models))
	}, [onClose, models])
	*/

	return (
		<L2Dialog 
			title={currentModel?.Product.Name}
			show={show} 
			size="max"
			headerProps={{ showBoxShadow: true }}
			showCloseButton={false}
		>
			<MedReconDialogContentContainer>
				<SearchResultsContainer>
					{
						currentModel?.Indications.length > 0 && (
							<CollapsedContentContainer>
								{
									currentModel?.Indications.map(code => (
										<SelectedResultRow key={code.Icd10Code}>
											<div>{code.Description}</div>
											<L1SmallIconButton color={FusionColor.BlueMain} icon={faTrashCan} onClick={() => handleChange(code.Icd10Code)} />
										</SelectedResultRow>
									))	
								}
							</CollapsedContentContainer>
						)
					}
					<L1TextField
						inputRef={textFieldRef}
						prefixIcon={faMagnifyingGlass} 
						size={ComplexElementSize.FlexStretch} 
						onChange={(e) => setSearchTerm(e.target.value)} 
						value={searchTerm} 
						tip={
							searchTerm !== '' && 
							<L1SmallIconButton 
								icon={faXmark} 
								color={FusionColor.BlueMain} 
								onClick={() => setSearchTerm('')} 
							/>
						} 
					/>
					{
						searchResults.length > 0 && (
							<SearchResultsList>
								<L3LazyLoadedList<DrugIcd>
									items={searchResults}
									itemIcrement={10}
									builder={(model, idx) => {
										return (
											<SearchResultRow showDivider={idx > 0}>
												<SearchResultRowText>
													{model.Description}
												</SearchResultRowText>
												<SearchResultRowActions>
													<L1ReasonableButton disabled={hasIndication(model.Icd10Code)} short color={FusionColor.BlueMain} label={hasIndication(model.Icd10Code) ? "Added" : "Add"} icon={faPlus} onClick={() => handleChange(model.Icd10Code, !!searchTerm)} />
												</SearchResultRowActions>
											</SearchResultRow>
										)
									}}
								/>
							</SearchResultsList>
						)
					}
				</SearchResultsContainer>
			</MedReconDialogContentContainer>
			<L2DialogActions justifyContent="center" showBoxShadow>
				{/*<L1ReasonableButton color={FusionColor.Text} disabled={hasPrev === false} icon={faAngleLeft} label="Prev Drug" onClick={() => handleNavigate(ModalNavEnum.Previous)} />*/}
				<L1ReasonableButton wide label="Done" onClick={handleSave} />
				{/*<L1ReasonableButton color={FusionColor.Text} disabled={hasNext === false} rightIcon={faAngleRight} label="Next Drug" onClick={() => handleNavigate(ModalNavEnum.Next)} />*/}
			</L2DialogActions>
    	</L2Dialog>
	)
}

export default L3MedReconIndicationsModal